/**
 * Runtime entry — bundles shared dependencies (React, React-DOM, React Query)
 * exposed on `window.HRWidgetRuntime` for cross-widget de-duplication.
 *
 * `mount` is deliberately NOT exported here. Each system widget IIFE and each
 * plugin IIFE imports `mount` from `@homerunner-next/widget-core/runtime` directly
 * and bundles it locally. That way mount upgrades ship via per-widget
 * rebuilds, not coordinated runtime redeploys.
 */

// React core
export { default as React } from "react";
export * as jsxRuntime from "react/jsx-runtime";
export {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
} from "react";

import { getDefaultJsUrls } from "../common/utils";

// React DOM
export { default as ReactDOM } from "react-dom";
export * from "react-dom";

// React DOM client (createRoot, hydrateRoot)
export * from "react-dom/client";
export { createRoot, hydrateRoot } from "react-dom/client";

// TanStack React Query. Widget IIFEs resolve `@tanstack/react-query` to this
// namespace (vite.config.ts RUNTIME_GLOBALS), so a symbol missing here is
// undefined in every served bundle while vitest/tsc still pass —
// test/runtime-export-surface.test.ts pins this list to what source imports
// (#1317: useQueries crashed the explorer, keepPreviousData silently no-oped).
export {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueries,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useIsFetching,
  useIsMutating,
  keepPreviousData,
  dehydrate,
  hydrate,
} from "@tanstack/react-query";
export type { DehydratedState } from "@tanstack/react-query";

export const proxyBaseUrl = process.env.NEXT_PUBLIC_WIDGET_ASSET_BASE_URL;

// HomeRunner Central public-api origin — Vite replaces process.env at build
// time. assets-dev.homerunner.io's runtime IIFE carries staging, assets's
// runtime carries production. Plugins read via `homerunnerApiBaseUrl()` from
// `@homerunner-next/widget-core/runtime`, which checks
// `window.HRWidgetRuntime.apiBaseUrl` first.
export const apiBaseUrl =
  process.env.NEXT_PUBLIC_HOMERUNNER_BASE_URL ?? "https://central.homerunner.io";

// Evergreen plugin lifecycle (docs/plugin-system/evergreen-runtime.md): the
// full widget-core runtime namespace, built from the workspace copy so it
// versions with the FLEET, not with any plugin. Plugin IIFEs built by
// `viteHomerunnerWidget` ≥ 0.10.0 externalize
// `@homerunner-next/widget-core/runtime` to `HRWidgetRuntime.widgetCore`, so
// `mount` (DSD adoption, SSR registry, reveal — the parts that kept breaking
// stale plugin builds) always executes at the fleet's version. First-party
// widget IIFEs still bundle their own copy — this export is the plugin path.
export * as widgetCore from "@homerunner-next/widget-core/runtime";

// Keyword → widget-IIFE URL, for layouts loading their slot children. System
// widgets get this injected by the embeddables `mount` wrapper; PLUGIN layout
// widgets mount through widget-core's mount directly (no wrapper), which
// falls back to `window.HRWidgetRuntime.resolveChildJsUrl` — the URL rules
// (hashed manifest, /p/ plugin proxy, `proxyBaseUrl` re-basing) live with
// this host bundle, never in plugin builds. `getDefaultJsUrls` is
// `[runtime, widget]`; children need the widget entry.
export const resolveChildJsUrl = (keyword: string): string => {
  const url = getDefaultJsUrls(keyword)[1]!;
  const origin = (window as unknown as { HRWidgetRuntime?: { proxyBaseUrl?: string } })
    .HRWidgetRuntime?.proxyBaseUrl;
  if (!origin) return url;
  try {
    const u = new URL(url);
    return `${origin.replace(/\/$/, "")}${u.pathname}${u.search}`;
  } catch {
    return url;
  }
};
